import React, { useEffect, useMemo, useState } from 'react';
import { listHrContracts, listHrPeople, listHrLppRates, peopleCostSynthesis } from '../../../api/hr';
import { formatNumber } from '../../../utils/format';
import { cellFteStyle } from '../fteColor';
import { monthlyMassSeries, extraMonths } from '../salaryMass';
import PrintReportHeader from './PrintReportHeader';
import { useP } from '../../../i18n/useP';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const ALL_M = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

function parseLocalDate(s) {
  if (!s) return null;
  const [y, m, d] = String(s).slice(0, 10).split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}
function fmtMonthYear(s) {
  if (!s) return '—';
  const d = parseLocalDate(s);
  if (!d) return '—';
  return `${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
}
function contractActiveInMonth(c, year, m) {
  if (!c.start_date) return false;
  const start = parseLocalDate(c.start_date);
  const monthStart = new Date(year, m, 1);
  const monthEnd   = new Date(year, m + 1, 0);
  if (start > monthEnd) return false;
  if (c.end_date) {
    const end = parseLocalDate(c.end_date);
    if (end < monthStart) return false;
  }
  return true;
}
function pickReferenceContract(contracts) {
  if (!contracts || contracts.length === 0) return null;
  const open = contracts.filter(c => c.end_date == null);
  const pool = open.length > 0 ? open : contracts;
  return pool.reduce((b, c) => (!b || (c.start_date || '') > (b.start_date || '') ? c : b), null);
}
function pickContractForMonth(contracts, year, m) {
  const active = contracts.filter(c => contractActiveInMonth(c, year, m));
  if (active.length === 0) return null;
  const open = active.filter(c => c.end_date == null);
  const pool = open.length > 0 ? open : active;
  return pool.reduce((b, c) => (!b || (c.start_date || '') > (b.start_date || '') ? c : b), null);
}
function buildCells(person, contracts, year) {
  const entry = person.entry_date ? parseLocalDate(person.entry_date) : null;
  const term  = person.termination_date ? parseLocalDate(person.termination_date) : null;
  return ALL_M.map(m => {
    const monthStart = new Date(year, m, 1);
    const monthEnd   = new Date(year, m + 1, 0);
    if (entry && entry > monthEnd)   return { kind: 'before', pct: 0 };
    if (term  && term  < monthStart) return { kind: 'after',  pct: 0 };
    const c = pickContractForMonth(contracts, year, m);
    if (!c) return { kind: 'none', pct: 0 };
    return { kind: 'active', pct: Math.round(Number(c.activity_rate || 0) * 100) };
  });
}

const fmtChf = (n) => formatNumber(Math.round(Number(n) || 0), 0);

export default function HrYearEndPrint({ activeTenant, year, editedAt, onDataReady }) {
  const p = useP();
  const tenantId = activeTenant?.id;
  const [contracts, setContracts] = useState([]);
  const [people, setPeople] = useState([]);
  const [lppRates, setLppRates] = useState([]);
  const [synth, setSynth] = useState(null);

  useEffect(() => {
    if (!tenantId) return;
    Promise.all([
      listHrContracts(tenantId),
      listHrPeople(tenantId),
      listHrLppRates(tenantId),
      peopleCostSynthesis(tenantId, year, 'all'),
    ]).then(([c, p, l, s]) => {
      if (!c.error) setContracts(c.data || []);
      if (!p.error) setPeople(p.data || []);
      if (!l.error) setLppRates(l.data || []);
      if (!s.error) setSynth(s.data || s);
      onDataReady && onDataReady('year-end');
    });
  }, [tenantId, year]); // eslint-disable-line

  const peopleView = useMemo(() => {
    const yearStart = new Date(year, 0, 1);
    const yearEnd   = new Date(year, 11, 31);
    const byPerson = new Map();
    for (const c of contracts) {
      if (!c.person_id) continue;
      if (!byPerson.has(c.person_id)) byPerson.set(c.person_id, []);
      byPerson.get(c.person_id).push(c);
    }
    return people
      .filter(p => {
        if (p.entry_date && parseLocalDate(p.entry_date) > yearEnd) return false;
        if (p.termination_date && parseLocalDate(p.termination_date) < yearStart) return false;
        return true;
      })
      .map(p => {
        const pcontracts = byPerson.get(p.id) || [];
        const ref = pickReferenceContract(pcontracts);
        const ar = ref ? Number(ref.activity_rate || 0) : 0;
        const ms = ref ? Number(ref.monthly_salary || 0) : 0;
        return {
          ...p,
          department: ref?.department || '',
          activityPct: ref ? Math.round(ar * 100) : null,
          salary100: (ref && ar > 0) ? ms / ar : 0,
          cells: buildCells(p, pcontracts, year),
        };
      })
      .sort((a, b) => (b.entry_date || '').localeCompare(a.entry_date || ''));
  }, [people, contracts, year]);

  const monthlyFte = useMemo(() => ALL_M.map(i =>
    peopleView.reduce((acc, p) => acc + (p.cells[i].kind === 'active' ? p.cells[i].pct / 100 : 0), 0),
  ), [peopleView]);
  const monthlyHc = useMemo(() => ALL_M.map(i =>
    peopleView.reduce((acc, p) => acc + (p.cells[i].kind === 'active' && p.cells[i].pct > 0 ? 1 : 0), 0),
  ), [peopleView]);

  const avgFte = monthlyFte.reduce((a, n) => a + n, 0) / 12;
  const avgHc  = monthlyHc.reduce((a, n) => a + n, 0)  / 12;

  const lppByPerson = useMemo(() => {
    const m = {};
    for (const r of lppRates) m[r.person_id] = (Number(r.lpp_employer_chf) || 0) + (Number(r.lpp_employee_chf) || 0);
    return m;
  }, [lppRates]);
  const lppTotal = peopleView.reduce((acc, p) => acc + (lppByPerson[p.id] || 0), 0);

  // Masse mensuelle (base + 13e positionné sur son mois de versement).
  const base = useMemo(
    () => monthlyMassSeries(contracts, year, extraMonths(activeTenant)),
    [contracts, year, activeTenant?.thirteenth_salary_month, activeTenant?.thirteenth_salary_month_2, activeTenant?.fourteenth_salary_month_13, activeTenant?.fourteenth_salary_month],
  );
  const annual = base.reduce((a, n) => a + n, 0);
  const csEmprRate = Number(synth?.cs_fallback_rate_empr || 0);
  const totalCost  = annual + annual * csEmprRate / 100;
  const avgMonthly = annual / 12;

  const yearSuffix = String(year).slice(-2);

  return (
    <section className="ppl-print__section ppl-print__section--wide">
      <PrintReportHeader category="People" title={p('Effectif')} year={year} tenant={activeTenant} editedAt={editedAt} />

      <div className="ppl-print__kpis">
        <Kpi label="Gross salaries" value={`${fmtChf(annual)} CHF`} hint="12-month total" />
        <Kpi label="Monthly average" value={`${fmtChf(avgMonthly)} CHF`} />
        <Kpi label="Total employer cost" value={`${fmtChf(totalCost)} CHF`} hint={`+${formatNumber(csEmprRate, 1)}% SC`} accent="primary" />
        <Kpi label="LPP estimate (er + ee)" value={`${fmtChf(lppTotal)} CHF`} hint="annual" />
        <Kpi label="Average annual FTE" value={formatNumber(avgFte, 1)} accent="sage" />
        <Kpi label="Average headcount" value={formatNumber(avgHc, 1)} hint={`peak ${Math.max(...monthlyHc)} persons`} />
      </div>

      <table className="ppl-print__table ppl-print__table--fixed ppl-print__table--dense ppl-print__table--total-once">
        <caption>Monthly grid per person · {peopleView.length} persons · active contract rate each month</caption>
        <colgroup>
          <col style={{ width: '40mm' }} />{/* Name */}
          <col style={{ width: '18mm' }} />{/* Dept */}
          <col style={{ width: '18mm' }} />{/* 100% CHF/m */}
          <col style={{ width: '16mm' }} />{/* Entry */}
          <col style={{ width: '16mm' }} />{/* Exit */}
          {ALL_M.map(i => <col key={i} style={{ width: '12.8mm' }} />)}
        </colgroup>
        <thead>
          <tr>
            <th>Name</th>
            <th>Dept</th>
            <th className="num nowrap">100% CHF/m</th>
            <th className="nowrap">Entry</th>
            <th className="nowrap">Exit</th>
            {ALL_M.map(i => (
              <th key={i} className="num nowrap">{MONTHS[i]}-{yearSuffix}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {peopleView.map((p, idx) => (
            <tr key={p.id} className={idx % 2 === 0 ? 'zebra' : ''}>
              <td><strong>{(p.last_name || '').toUpperCase()}</strong> {p.first_name || ''}</td>
              <td>{p.department ? <span className="ppl-print__badge">{p.department}</span> : '—'}</td>
              <td className="num nowrap">{p.salary100 > 0 ? fmtChf(p.salary100) : '—'}</td>
              <td className="nowrap">{fmtMonthYear(p.entry_date)}</td>
              <td className="nowrap">{fmtMonthYear(p.termination_date)}</td>
              {p.cells.map((cell, i) => (
                <td key={i} className="num nowrap" style={cellFteStyle(cell.kind, cell.pct)}>
                  {cell.pct}%
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={5} className="nowrap">Total FTE <span style={{ color: '#777', fontWeight: 400, marginLeft: '4mm' }}>avg {formatNumber(avgFte, 1)}</span></td>
            {monthlyFte.map((v, i) => (
              <td key={i} className="num nowrap">{formatNumber(v, 1)}</td>
            ))}
          </tr>
          <tr>
            <td colSpan={5} className="nowrap">Total Headcount <span style={{ color: '#777', fontWeight: 400, marginLeft: '4mm' }}>avg {formatNumber(avgHc, 1)}</span></td>
            {monthlyHc.map((v, i) => (
              <td key={i} className="num nowrap">{v}</td>
            ))}
          </tr>
        </tfoot>
      </table>
    </section>
  );
}

function Kpi({ label, value, hint, accent }) {
  const cls = `ppl-print__kpi${accent ? ` ppl-print__kpi--${accent}` : ''}`;
  return (
    <div className={cls}>
      <div className="ppl-print__kpi-label">{label}</div>
      <div className="ppl-print__kpi-value">{value}</div>
      {hint && <div className="ppl-print__kpi-hint">{hint}</div>}
    </div>
  );
}
